import React from 'react';
import { Card, Table, Button, Row, Col, Statistic, Tag, Space, Select, DatePicker, Empty } from 'antd';
import { CarOutlined, DollarOutlined, ClockCircleOutlined, EnvironmentOutlined, StarOutlined } from '@ant-design/icons';

import dayjs from 'dayjs';
import { useIsMobile } from '../hooks/useIsMobile';

interface Delivery {
  id: number;
  orderId: number;
  employer: string;
  employerType: string;
  fromAddress: string;
  toAddress: string;
  distance: number;
  startTime: string;
  endTime: string;
  duration: number;
  rating: number;
  appliedRate: number;
  speedBonus: number;
  penalties: number;
  totalEarning: number;
  status: string;
}

const DeliveryDashboard: React.FC = () => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = React.useState(false);
  const [deliveries, setDeliveries] = React.useState<Delivery[]>([]);
  const [selectedPeriod, setSelectedPeriod] = React.useState('this_month');
  const [range, setRange] = React.useState({
    start: dayjs().startOf('month'),
    end: dayjs().endOf('month')
  });

  // Тестовые данные
  const testDeliveries: Delivery[] = [
    {
      id: 1,
      orderId: 123,
      employer: 'ООО "Вкусно"',
      employerType: 'supplier',
      fromAddress: 'Москва, ул. Ленина, 1',
      toAddress: 'Москва, пр. Мира, 10',
      distance: 2.5,
      startTime: new Date().toISOString(),
      endTime: new Date(Date.now() + 15 * 60000).toISOString(),
      duration: 15,
      rating: 5.0,
      appliedRate: 80,
      speedBonus: 30,
      penalties: 0,
      totalEarning: 110,
      status: 'completed'
    },
    {
      id: 2,
      orderId: 124,
      employer: 'ООО "Офис+"',
      employerType: 'customer',
      fromAddress: 'Москва, ул. Ленина, 1',
      toAddress: 'Москва, ул. Победы, 50',
      distance: 8.5,
      startTime: new Date(Date.now() - 3600000).toISOString(),
      endTime: new Date(Date.now() - 3300000).toISOString(),
      duration: 28,
      rating: 4.5,
      appliedRate: 150,
      speedBonus: 30,
      penalties: 0,
      totalEarning: 180,
      status: 'completed'
    },
    {
      id: 3,
      orderId: 125,
      employer: 'ООО "Вкусно"',
      employerType: 'supplier',
      fromAddress: 'Москва, ул. Ленина, 1',
      toAddress: 'Москва, ул. Строителей, 5',
      distance: 4.2,
      startTime: new Date(Date.now() - 7200000).toISOString(),
      endTime: new Date(Date.now() - 4500000).toISOString(),
      duration: 45,
      rating: 3.0,
      appliedRate: 120,
      speedBonus: 0,
      penalties: -200,
      totalEarning: -80,
      status: 'completed'
    }
  ];

  React.useEffect(() => {
    load();
  }, []);

  const load = () => {
    const stored = localStorage.getItem('my_deliveries');
    if (stored) {
      setDeliveries(JSON.parse(stored));
    } else {
      setDeliveries(testDeliveries);
      localStorage.setItem('my_deliveries', JSON.stringify(testDeliveries));
    }
  };

  const handlePeriodChange = (value: string) => {
    setSelectedPeriod(value);
    let newRange = { start: range.start, end: range.end };
    
    switch (value) {
      case 'today':
        newRange = { start: dayjs().startOf('day'), end: dayjs().endOf('day') };
        break;
      case 'this_week':
        newRange = { start: dayjs().startOf('week'), end: dayjs().endOf('week') };
        break;
      case 'this_month':
        newRange = { start: dayjs().startOf('month'), end: dayjs().endOf('month') };
        break;
      case 'last_month':
        newRange = { start: dayjs().subtract(1, 'month').startOf('month'), end: dayjs().subtract(1, 'month').endOf('month') };
        break;
    }
    
    setRange(newRange);
  };

  // Статистика
  const totalDeliveries = deliveries.filter(d => d.status === 'completed').length;
  const totalEarnings = deliveries.reduce((sum, d) => sum + d.totalEarning, 0);
  const avgRating = deliveries.length > 0
    ? deliveries.filter(d => d.rating).reduce((sum, d) => sum + (d.rating || 0), 0) / deliveries.filter(d => d.rating).length
    : 0;
  const todayDeliveries = deliveries.filter(d => 
    dayjs(d.startTime).isSame(dayjs(), 'day')
  ).length;
  const todayEarnings = deliveries.filter(d => 
    dayjs(d.startTime).isSame(dayjs(), 'day')
  ).reduce((sum, d) => sum + d.totalEarning, 0);

  // Группировка по работодателям
  const earningsByEmployer = deliveries.reduce((acc: any, d) => {
    if (!acc[d.employer]) {
      acc[d.employer] = { deliveries: 0, earnings: 0 };
    }
    acc[d.employer].deliveries += 1;
    acc[d.employer].earnings += d.totalEarning;
    return acc;
  }, {});

  const columns = [
    {
      title: '№',
      dataIndex: 'orderId',
      key: 'orderId',
      width: 80,
      render: (id: number) => `#${id}`
    },
    {
      title: 'Работодатель',
      dataIndex: 'employer',
      key: 'employer',
      width: 150,
      render: (employer: string, record: Delivery) => (
        <div>
          <div style={{ fontWeight: 500 }}>{employer}</div>
          <Tag color={record.employerType === 'supplier' ? 'green' : 'blue'} style={{ fontSize: '11px' }}>
            {record.employerType === 'supplier' ? 'Поставщик' : 'Заказчик'}
          </Tag>
        </div>
      )
    },
    {
      title: 'Маршрут',
      key: 'route',
      ellipsis: true,
      render: (_: any, record: Delivery) => (
        <div style={{ fontSize: '12px' }}>
          <div style={{ color: '#666' }}>📍 {record.fromAddress.split(',').slice(-2).join(',')}</div>
          <div style={{ color: '#999' }}>→ {record.toAddress.split(',').slice(-2).join(',')}</div>
        </div>
      )
    },
    {
      title: 'Расстояние',
      dataIndex: 'distance',
      key: 'distance',
      width: 100,
      render: (distance: number) => (
        <span style={{ fontSize: '12px' }}>{distance} км</span>
      )
    },
    {
      title: 'Время',
      dataIndex: 'duration',
      key: 'duration',
      width: 80,
      render: (duration: number, record: Delivery) => {
        const isDelayed = duration > 60;
        return (
          <Tag color={isDelayed ? 'red' : duration < 30 ? 'green' : 'default'}>
            {duration} мин
          </Tag>
        );
      }
    },
    {
      title: 'Рейтинг',
      dataIndex: 'rating',
      key: 'rating',
      width: 90,
      render: (rating: number) => (
        rating ? (
          <span style={{ fontSize: '13px', fontWeight: 600, color: rating >= 4.5 ? '#52c41a' : rating >= 3.5 ? '#faad14' : '#ff4d4f' }}>
            ⭐ {rating.toFixed(1)}
          </span>
        ) : '-'
      )
    },
    {
      title: 'Заработок',
      key: 'earnings',
      width: 150,
      render: (_: any, record: Delivery) => (
        <div>
          <div style={{ fontSize: '14px', fontWeight: 600, color: record.totalEarning > 0 ? '#52c41a' : '#ff4d4f' }}>
            {record.totalEarning > 0 ? '+' : ''}{record.totalEarning}₽
          </div>
          {(record.speedBonus > 0 || record.penalties < 0) && (
            <div style={{ fontSize: '11px', color: '#999' }}>
              {record.speedBonus > 0 && <span style={{ color: '#52c41a' }}>+{record.speedBonus}₽ </span>}
              {record.penalties < 0 && <span style={{ color: '#ff4d4f' }}>{record.penalties}₽</span>}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Дата',
      dataIndex: 'startTime',
      key: 'startTime',
      width: 150,
      render: (time: string) => (
        <div style={{ fontSize: '12px' }}>
          <div>{dayjs(time).format('DD.MM.YYYY')}</div>
          <div style={{ color: '#999' }}>{dayjs(time).format('HH:mm')}</div>
        </div>
      ),
      sorter: (a: Delivery, b: Delivery) => 
        new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
    }
  ];

  return (
    <>
    
      <Row gutter={16} style={{ marginBottom: '16px' }}>
        <Col span={24}>
          <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
            <Select
              value={selectedPeriod}
              onChange={handlePeriodChange}
              options={[
                { value: 'today', label: 'Сегодня' },
                { value: 'this_week', label: 'Эта неделя' },
                { value: 'this_month', label: 'Этот месяц' },
                { value: 'last_month', label: 'Прошлый месяц' },
              ]}
              style={{ width: isMobile ? '100%' : 200 }}
            />
          </Space>
        </Col>
      </Row>

      {/* Статистика за период */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Доставок за период"
              value={totalDeliveries}
              prefix={<CarOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Заработок за период"
              value={totalEarnings}
              suffix="₽"
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Средний рейтинг"
              value={avgRating.toFixed(1)}
              suffix="/5"
              prefix={<StarOutlined />}
              valueStyle={{ color: avgRating >= 4.5 ? '#52c41a' : '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Сегодня"
              value={todayDeliveries}
              suffix={`дост. • ${todayEarnings}₽`}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#722ed1', fontSize: '18px' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Разбивка по работодателям */}
      <Card title="Заработок по работодателям" style={{ marginBottom: '16px' }}>
        <Row gutter={16}>
          {Object.entries(earningsByEmployer).map(([employer, stats]: [string, any]) => (
            <Col key={employer} xs={24} sm={12} md={8}>
              <div style={{ 
                padding: '12px', 
                border: '1px solid #f0f0f0', 
                borderRadius: '6px',
                background: '#fafafa'
              }}>
                <div style={{ fontWeight: 500, marginBottom: '8px' }}>{employer}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  Доставок: {stats.deliveries}
                </div>
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#52c41a', marginTop: '4px' }}>
                  {stats.earnings.toLocaleString('ru')}₽
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Таблица доставок */}
      <Card title="История доставок">
        {deliveries.length > 0 ? (
          <Table
            rowKey="id"
            columns={columns as any}
            dataSource={deliveries}
            pagination={{ pageSize: 15 }}
            scroll={isMobile ? { x: 'max-content' } : { x: 1200 }}
            size={isMobile ? 'small' : 'middle'}
          />
        ) : (
          <Empty description="Нет доставок за выбранный период" style={{ margin: '40px 0' }} />
        )}
      </Card>
    </>
  );
};

export default DeliveryDashboard;

